import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Util from 'utility';
import { __ } from 'locale';

import {
    setAppSubMode,
    setLectureTaskComplete,
    setLectureTaskCompleteSaved,
    openModal,
    closeModal,
    setLastSavedAt
} from 'actions/index';

import evaluateTask from './TaskEvaluator';
import EvaluationPanel from 'components/EvaluationPanel';
import SeminarWorkHeader from './SeminarWorkHeader';

function arrayEqualUnordered(a, b) {
    if (a.length !== b.length) return false;
    const left = [...a].sort();
    const right = [...b].sort();
    return left.every((value, index) => value === right[index]);
}

function getNutriKucharEvaluationDetails(data) {
    const er = data.evaluationResultsNk;
    if (!er || typeof er !== 'object' || !er.results) {
        return {
            requiredFoods: [],
            missingFoods: [],
            wrongAmount: [],
            wrongPreparations: [],
            excessFoods: [],
            allFoodsOk: false,
        };
    }

    const requiredItems = Array.isArray(er.results.required) ? er.results.required : [];
    const excessFoods = Array.isArray(er.results.excess)
        ? er.results.excess.map(item => item.title)
        : [];

    const requiredFoods = [];
    const missingFoods = [];
    const wrongAmount = [];
    const wrongPreparations = [];

    requiredItems.forEach(item => {
        const requiredPreparations = Array.isArray(item.preparations) ? item.preparations : [];
        const normalizedRequiredPreparations = (requiredPreparations.length === 1 && requiredPreparations[0] == 0)
            ? []
            : requiredPreparations.map(value => value * 1);
        const prepTitles = normalizedRequiredPreparations.map(preparationId => data.globals.preparation_types[preparationId]);

        requiredFoods.push({
            isOk: item.is_ok === true,
            title: `${item.title} (${item.amount} ${item.weight_unit})`,
            preparations: prepTitles,
        });

        if (item.missing) {
            missingFoods.push(`${item.title} (${item.amount} ${item.weight_unit})`);
        }

        if (item.user_amount != item.amount) {
            wrongAmount.push(`${item.title} (${item.user_amount} ${item.weight_unit} -> ${item.amount} ${item.weight_unit})`);
        }

        let hasMatchingPreparation = false;

        Object.keys(data.foods).forEach(mealHash => {
            const mealFoods = data.foods[mealHash];
            if (!Array.isArray(mealFoods)) return;

            mealFoods.forEach(food => {
                if (food.food_id != item.item_id) return;
                const userPreparations = Array.isArray(data.modifications[food.id])
                    ? data.modifications[food.id].map(value => value * 1)
                    : [];
                if (arrayEqualUnordered(userPreparations, normalizedRequiredPreparations)) {
                    hasMatchingPreparation = true;
                }
            });
        });

        if (!hasMatchingPreparation) {
            wrongPreparations.push(
                prepTitles.length > 0
                    ? `${item.title} (${prepTitles.join(', ')})`
                    : `${item.title} (${__('žádné úpravy')})`
            );
        }
    });

    return {
        requiredFoods,
        missingFoods,
        wrongAmount,
        wrongPreparations,
        excessFoods,
        allFoodsOk: requiredItems.every(item => item.is_ok === true),
    };
}

function getNutriKucharCompletionDiagnostics(data, evaluation, allFoodGroupsHaveNames, allFoodGroupsHaveDescriptions, missingNamesMealLabels, missingDescriptionsMealLabels) {
    const evaluationResults = data.evaluationResultsNk?.results;
    if (!evaluationResults) return null;

    const requiredItems = Array.isArray(evaluationResults.required) ? evaluationResults.required : [];
    const preparationTypes = data.globals.preparation_types || {};
    const invalidFoods = requiredItems
        .filter(item => item.is_ok !== true)
        .map(item => {
            const expectedPreparations = (Array.isArray(item.preparations) ? item.preparations : [])
                .filter(preparationId => preparationId != 0)
                .map(preparationId => preparationTypes[preparationId] || preparationId);
            const reasons = [];

            if (item.user_amount == 0) {
                reasons.push('potravina v receptu chybí');
            } else if (item.checks?.amount === false || item.user_amount != item.amount) {
                reasons.push(`množství ${item.user_amount} místo ${item.amount} ${item.weight_unit}`);
            }
            if (item.checks?.preparations === false) {
                reasons.push(`úpravy neodpovídají požadavku: ${expectedPreparations.join(', ') || 'žádné úpravy'}`);
            }
            if (reasons.length === 0) {
                reasons.push('potravina neprošla kontrolou');
            }

            return {
                potravina: item.title,
                důvody: reasons,
            };
        });

    const excessFoods = Array.isArray(evaluationResults.excess)
        ? evaluationResults.excess.map(item => `${item.title} (${item.amount} ${item.weight_unit})`)
        : [];

    return {
        požadovanéPotravinyNačteny: requiredItems.length,
        nevyhovujícíPotraviny: invalidFoods,
        nadbytečnéPotraviny: excessFoods,
        chybíNázevReceptu: !allFoodGroupsHaveNames,
        chybíPostupPřípravy: !allFoodGroupsHaveDescriptions,
        chodyBezNázvu: missingNamesMealLabels,
        chodyBezPostupu: missingDescriptionsMealLabels,
        klientskéDůvody: {
            chybějícíPotraviny: evaluation.missingFoods,
            špatnéMnožství: evaluation.wrongAmount,
            špatnéÚpravy: evaluation.wrongPreparations,
        },
    };
}

function LectureTaskPlan({
    lectureInfo, plan, viewTypeSwitch2,
    openSaveLectureTaskPanel, openLoadLectureTaskPanel,
    openSeminarWorkLongtermEvaluationPanel,
    openAssignmentInfo, openLectureTaskSampleText, openProfileInfo,
}) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();

    const [showSaveWarning, setShowSaveWarning] = useState(false);
    const [lastSaveWarningTime, setLastSaveWarningTime] = useState(0);
    const [mobileTab, setMobileTab] = useState('main');
    const tickRef = useRef(null);
    const nutriKucharDiagnosticsRef = useRef('');
    const isNutriKucharMode = Util.isNutriKucharMode(data.appMode);
    const isSampleTask = data.appSubMode === 'LECTURETASK_SAMPLE';
    const lectureInfoData = data.appModeData?.lectureInfoData;
    const isSingleDish = Util.isSingleDishLectureTask(lectureInfoData);
    const taskNounNom = Util.getLectureTaskDishTerm(lectureInfoData, 'nom');
    const taskNounAcc = Util.getLectureTaskDishTerm(lectureInfoData, 'acc');
    const taskNounGen = Util.getLectureTaskDishTerm(lectureInfoData, 'gen');
    const taskNounLoc = Util.getLectureTaskDishTerm(lectureInfoData, 'loc');
    const taskNounIns = Util.getLectureTaskDishTerm(lectureInfoData, 'ins');


    const tick = () => {
        const ld = data.lastSavedAt / 1000;
        const now = Date.now() / 1000;
        const diff = now - ld;
        if (diff > 180) {
            setShowSaveWarning(true);
            setLastSaveWarningTime(now);
            dispatch(setLastSavedAt(Date.now()));
        } else {
            if (showSaveWarning) {
                if ((now - lastSaveWarningTime) > 6) {
                    setShowSaveWarning(false);
                }
            }
        }
    };
    tickRef.current = tick;

    useEffect(() => {
        dispatch(setLastSavedAt(Date.now()));
        const timerID = setInterval(() => tickRef.current(), 2000);
        return () => clearInterval(timerID);
    }, []); // eslint-disable-line

    const openLectureTaskSampleMenu = () => {
        const lectureData = data.appModeData.lectureInfoData.lecture_data;
        const sampleTaskId = isNutriKucharMode && lectureData.sample_recipe_ids
            ? lectureData.sample_recipe_ids
            : lectureData.sample_lecturetask_id;
        let id = sampleTaskId;
        id = 8800000 + id * 7;
        dispatch(setAppSubMode('LECTURETASK_SAMPLE'));
        history.push('/load/task/' + id + '/' + data.appModeData.lectureId);
    };

    const showTaskListDDDInfoFunc = (info, id, type) => {
        const items = type === 'category' ? info['categories'][id] : info['tags'][id];
        const title = type === 'category'
            ? __('Chody/potraviny obsahující') + ' "' + data.globals.foodCategories[id].title + '"'
            : __('Chody/potraviny obsahující') + ' "' + data.globals.foodTags[id] + '"';

        let rows = [];
        if (typeof items !== 'undefined') {
            let tmp = {};
            items.map(f => {
                const mk = f.meal + '|' + f.meal_id;
                if (typeof tmp[mk] === 'undefined') tmp[mk] = {};
                if (typeof tmp[mk][f.baseFood.title] === 'undefined') tmp[mk][f.baseFood.title] = [];
                if (f.baseFood.title !== f.food.title) {
                    tmp[mk][f.baseFood.title].push(f.food.title);
                }
            });

            Object.keys(data.globals.mealTypes).map(m => {
                const mt = data.globals.mealTypes[m].title;
                const mk = mt + '|' + m;
                if (typeof tmp[mk] !== 'undefined') {
                    rows.push(<h2 className="font-semibold mb-2" key={mk}>{mt}</h2>);
                    let subrows = [];
                    Object.keys(tmp[mk]).map(fd => {
                        const subFoods = tmp[mk][fd];
                        subrows.push(<p key={mk + fd} className="m-0">{subFoods.length > 0 ? `${fd}: ${subFoods.join(', ')}` : fd}</p>);
                    });
                    rows.push(<div className="text-sm mb-4" key={mk + 'div'}>{subrows}</div>);
                }
            });
        }

        if (rows.length === 0) {
            rows.push(<p className="italic text-gray-500" key="noitems">{__('Žádné odpovídající chody / potraviny')}</p>);
        }

        dispatch(closeModal());
        dispatch(openModal({
            title,
            closeButton: __('Zavřít'),
            class: 'dialog-flexible',
            content: <div>{rows}</div>,
        }));
    };

    const { globals } = data;
    const taskData = data.lectureTaskData;

    let evaluationPanelBeforeContent = false;

    let profileId = Util.getActiveProfileId();
    if (typeof data.appModeData.profileId === 'number') {
        profileId = data.appModeData.profileId;
    }

    let evalResults = false;
    if (typeof data.evaluationResults === 'object') {
        evalResults = data.evaluationResults.results;
    }

    let tasksList = [];
    let taskCompletedInfo = false;

    if (typeof taskData.lecture_data === 'object') {

        const res = evaluateTask(data, { 'dddInfo': showTaskListDDDInfoFunc }, { lectureInfo });

        let taskListMeals = res.taskListMeals;
        let taskListMealsOk = res.taskListMealsOk;
        let taskListUndesirableFoodsUsed = res.taskListUndesirableFoodsUsed;
        let taskListNumberOfFoods = res.taskListNumberOfFoods;
        let taskListNumberOfFoodsOk = res.taskListNumberOfFoodsOk;
        let taskListDDD = res.taskListDDD;
        let taskListDDDOk = res.taskListDDDOk;

        const taskBoxCls = 'mb-4';
        const taskBoxHeaderCls = 'flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200';
        const taskBoxTitleCls = 'text-xs m-0 text-slate-500 uppercase font-extrabold tracking-wider';
        const taskHelpLabelCls = 'cursor-pointer flex items-center justify-center w-4 h-4 border border-slate-300 text-slate-400 text-xs rounded-full leading-none flex-shrink-0';
        const taskHelpPanelCls = 'hidden peer-checked:block absolute bg-white text-xs p-4 rounded-xl left-0 top-[calc(100%+0.5em)] border border-slate-200 shadow-lg z-[10000] w-64';

        let warningText1 = 'Vytvořte u každého chodu recept/y odpovídající zadané chuti.';
        let warningText2 = `Vámi vytvořený ${taskNounNom} musí obsahovat požadované počty zástupců potravin z jednotlivých kategorií.`;
        let warningText3 = `Vámi vytvořený ${taskNounNom} musí splňovat požadované nutriční hodnoty.`;
        let warningText4 = `Ve vytvořeném ${taskNounLoc} sledujte vybrané nutriční hodnoty. Tyto slouží jako informační doplněk k hodnocení jeho celkové kvality.`;
        let warningText5 = `Ve vytvořeném ${taskNounLoc} sledujte poměry makroživin hodnocení v jejich procentuálním podílu z celkového příjmu energie.`;

        if (Util.getLang() == 'sk') {
            warningText1 = 'Vytvorte pri každom chode recept/y zodpovedajúce zadanej chuti.';
            warningText2 = `Vami vytvorený ${taskNounNom} musí obsahovať požadované počty zástupcov potravín z jednotlivých kategórií.`;
            warningText3 = `Vami vytvorený ${taskNounNom} musí spĺňať požadované nutričné ​​hodnoty.`;
            warningText4 = `Vo vytvorenom ${taskNounLoc} sledujte vybrané nutričné ​​hodnoty. Tieto slúžia ako informačný doplnok na hodnotenie jeho celkovej kvality.`;
            warningText5 = `Vo vytvorenom ${taskNounLoc} sledujte pomery makroživín hodnotenia v ich percentuálnom podiele z celkového príjmu energie.`;
        }


        if (taskListMeals.length > 0 || taskListMealsOk.length > 0) {
            let taskListMealsEl = (
                <>
                    {taskListMeals}
                    {taskListMealsOk}
                </>
            );

            if (Util.isCurrentTaskSeminarWorkAssignment()) {
                taskListMealsEl = (
                    <div className="[&_span]:hidden [&_p]:ml-0">
                        {taskListMeals}
                        {taskListMealsOk}
                    </div>
                );
            }

            tasksList.push(
                <div key="box-meals" className={taskBoxCls}>
                    <div className={`${taskBoxHeaderCls} relative`}>
                        <h3 className={taskBoxTitleCls}>{__('Chody / chuť')}</h3>
                        <label className={taskHelpLabelCls} htmlFor="lt-cb1">?</label>
                        <input className="peer hidden" type="checkbox" id="lt-cb1" />
                        <div className={taskHelpPanelCls}>
                            <p className="m-0">{warningText1}</p>
                        </div>
                    </div>
                    {taskListMealsEl}
                </div>
            );
        }

        if (taskListNumberOfFoods.length > 0 || taskListNumberOfFoodsOk.length > 0) {
            tasksList.push(
                <div key="box-variety" className={taskBoxCls}>
                    <div className={`${taskBoxHeaderCls} relative`}>
                        <h3 className={taskBoxTitleCls}>{__('Pestrost')}</h3>
                        <label className={taskHelpLabelCls} htmlFor="lt-cb2">?</label>
                        <input className="peer hidden" type="checkbox" id="lt-cb2" />
                        <div className={taskHelpPanelCls}>
                            <p className="m-0">{warningText2}</p>
                        </div>
                    </div>
                    {taskListNumberOfFoods}
                    {taskListUndesirableFoodsUsed}
                    {taskListNumberOfFoodsOk}
                </div>
            );
        }

        if (taskListDDD.length > 0 || taskListDDDOk.length > 0) {
            tasksList.push(
                <div key="box-nutrition" className={taskBoxCls}>
                    <div className={`${taskBoxHeaderCls} relative`}>
                        <h3 className={taskBoxTitleCls}>{__('Nutriční hodnoty')}</h3>
                        <label className={taskHelpLabelCls} htmlFor="lt-cb3">?</label>
                        <input className="peer hidden" type="checkbox" id="lt-cb3" />
                        <div className={taskHelpPanelCls}>
                            <p className="m-0">{warningText3}</p>
                        </div>
                    </div>
                    {taskListDDD}
                    {taskListDDDOk}
                </div>
            );
        }

        let allFoodGroupsHaveNames = true;
        let allFoodGroupsHaveDescriptions = true;

        const requiredMealTypes = Array.isArray(taskData?.lecture_data?.meal_types)
            ? [...new Set(taskData.lecture_data.meal_types
                .map(mt => parseInt(mt?.type, 10))
                .filter(mealTypeId => Number.isFinite(mealTypeId) && mealTypeId > 0))]
            : [];
        const describedMealTypes = new Set();
        const mealTypesWithMissingNames = new Set();

        const formatMealTypeLabels = (mealTypeIds) => mealTypeIds
            .map(mealTypeId => globals.mealTypes?.[mealTypeId]?.title || `Chod ${mealTypeId}`)
            .join(', ');

        Object.keys(data.foodGroups).map(m => {
            let ma = m.split('|');
            if (ma[0] === "0") return;
            const mealTypeId = ma[0] * 1;
            const fgm = data.foodGroups[m];
            let hasMissingNameInMeal = false;
            Object.keys(fgm).map(fg => {
                const g = fgm[fg];
                if (g.title.trim() == '') {
                    allFoodGroupsHaveNames = false;
                    hasMissingNameInMeal = true;
                }

                if (typeof g.description === 'string') {
                    if (g.description.trim() != '') describedMealTypes.add(mealTypeId);
                }
            });
            if (hasMissingNameInMeal) {
                mealTypesWithMissingNames.add(mealTypeId);
            }
        });

        const mealTypesWithMissingDescriptions = requiredMealTypes.filter(mealTypeId => !describedMealTypes.has(mealTypeId));

        if (requiredMealTypes.length > 0) {
            allFoodGroupsHaveDescriptions = mealTypesWithMissingDescriptions.length === 0;
        }

        const missingNamesMealLabels = formatMealTypeLabels([...mealTypesWithMissingNames].sort((a, b) => a - b));
        const missingDescriptionsMealLabels = formatMealTypeLabels(mealTypesWithMissingDescriptions);

        let allFoodGroupsMustHaveNamesWarning = false;
        let allFoodGroupsMustHaveDescriptionsWarning = false;

        const isCurrentlyCompleted = data.lectureTaskComplete;

        const liData = data.appModeData.lectureInfoData;

        if ((typeof liData.lecture_data.custom_settings !== 'undefined') &&
            (typeof liData.lecture_data.custom_settings === 'object') &&
            liData.lecture_data.custom_settings !== null &&
            liData.lecture_data.custom_settings.includes('one_food_to_complete_task')) {
            let foodsNum = 0;
            Object.keys(data.foods).map(f => {
                foodsNum += data.foods[f].length;
            });
            if (foodsNum > 0) {
                if (isCurrentlyCompleted !== true) {
                    dispatch(setLectureTaskComplete(true));
                }
            }

        } else if (!isNutriKucharMode) {

            if (
                taskListMeals.length === 0 &&
                taskListNumberOfFoods.length === 0 &&
                taskListDDD.length === 0 &&
                taskListUndesirableFoodsUsed.length === 0
            ) {
                if (Util.isCurrentTaskSeminarWork() && !Util.isCurrentTaskSeminarWorkAssignment()) {
                    if (allFoodGroupsHaveNames && allFoodGroupsHaveDescriptions) {
                        if (isCurrentlyCompleted !== true) {
                            dispatch(setLectureTaskComplete(true));
                        }
                    } else {
                        if (!allFoodGroupsHaveNames) {
                            allFoodGroupsMustHaveNamesWarning = (
                                <div className="mx-4 mt-3 p-2 rounded-lg bg-red-50 border border-red-200 text-center">
                                    <p className="text-xs font-bold text-red-600 m-0">{__('Doplňte názvy ke všem receptům!')}</p>
                                    {missingNamesMealLabels !== '' && <p className="text-[12px] text-red-500 m-0 mt-1">Chody: {missingNamesMealLabels}</p>}
                                </div>
                            );
                        }
                        if (!allFoodGroupsHaveDescriptions) {
                            allFoodGroupsMustHaveDescriptionsWarning = (
                                <div className="mx-4 mt-3 p-2 rounded-lg bg-red-50 border border-red-200 text-center">
                                    <p className="text-xs font-bold text-red-600 m-0">{__('Doplňte popisy přípravy ke všem receptům!')}</p>
                                    {missingDescriptionsMealLabels !== '' && <p className="text-[12px] text-red-500 m-0 mt-1">Chody: {missingDescriptionsMealLabels}</p>}
                                </div>
                            );
                        }

                        if (isCurrentlyCompleted !== false) {
                            dispatch(setLectureTaskComplete(false));
                        }
                    }

                } else {
                    if (allFoodGroupsHaveNames) {
                        if (isCurrentlyCompleted !== true) {
                            dispatch(setLectureTaskComplete(true));
                        }
                    } else {
                        allFoodGroupsMustHaveNamesWarning = (
                            <div className="mx-4 mt-3 p-2 rounded-lg bg-red-50 border border-red-200 text-center">
                                <p className="text-xs font-bold text-red-600 m-0">{__('Doplňte názvy ke všem receptům!')}</p>
                                {missingNamesMealLabels !== '' && <p className="text-[11px] text-red-500 m-0 mt-1">Chody: {missingNamesMealLabels}</p>}
                            </div>
                        );
                        if (isCurrentlyCompleted !== false) {
                            dispatch(setLectureTaskComplete(false));
                        }
                    }
                }

            } else {
                if (isCurrentlyCompleted !== false) {
                    dispatch(setLectureTaskComplete(false));
                }
            }

        }

        let seminarWorkData = data.appModeData?.lectureInfoData?.seminar_work_data;

        let watchAttribs = [];
        let graph = false;

        let showRI = [
            'potassium', 'calcium', 'magnesium', 'sodium', 'iodine', 'selenium', 'zinc', 'iron', 'iron_hem', 'phosphorus', 'zinc',
            'vitamin_c', 'vitamin_d', 'vitamin_e', 'vitamin_d2', 'vitamin_d3', 'vitamin_a', 'vitamin_b1', 'vitamin_b2', 'vitamin_b3',
            'vitamin_b5', 'vitamin_b6', 'vitamin_b9', 'vitamin_b12', 'vitamin_k'
        ];

        let showRatios = {
            'proteins_ratio': { 'after': 'proteins_plant', 'depends_on': ['proteins_animal', 'proteins_plant'] },
            'fats_saturated_unsaturated_ratio': { 'after': 'fats_unsaturated_total', 'depends_on': ['fats_saturated', 'fats_unsaturated_total'] },
            'fats_monounsaturated_polyunsaturated_ratio': { 'after': 'fats_polyunsaturated', 'depends_on': ['fats_monounsaturated', 'fats_polyunsaturated'] },
            'fats_omega3_omega6_ratio': { 'after': 'fats_omega6', 'depends_on': ['fats_omega3', 'fats_omega6'] },
            'fats_omega3_dha_epa_omega6_ratio': { 'after': 'fats_omega6', 'depends_on': ['fats_omega3_dha_epa', 'fats_omega6'] },
            'fiber_ratio': { 'after': 'fiber_soluble', 'depends_on': ['fiber_soluble', 'fiber_insoluble'] },
            'calcium_magnesium_ratio': { 'after': 'magnesium', 'depends_on': ['calcium', 'magnesium'] },
            'potassium_sodium_ratio': { 'after': 'sodium', 'depends_on': ['potassium', 'sodium'] },
            'calcium_phosphorus_ratio': { 'after': 'phosphorus', 'depends_on': ['calcium', 'phosphorus'] },
        };

        if (typeof evalResults !== 'undefined') {
            if (Array.isArray(Object.keys(taskData.ddd_limits))) {
                Object.keys(taskData.ddd_limits).map(attr => {
                    if (attr == '') return;
                    if (attr === 'fats_unsaturated') attr = 'fats_unsaturated_total';
                    Object.keys(showRatios).map(r => {
                        if (showRatios[r].depends_on.includes(attr)) {
                            showRatios[r].depends_on = showRatios[r].depends_on.filter(a => a !== attr);
                        }
                    });
                });
            }
        }

        let watchAttribsAlreadyFilled = [];
        let watchAttribsByKey = {};
        let currEl;

        let watchAttrs = data.appModeData.watchAttributes;
        if (typeof watchAttrs === 'object') {
            if (Util.isCurrentTaskSeminarWork()) {
                watchAttrs.push('weight');
                watchAttrs.push('drink_amount');
            }
        }

        let energyAmountText = 'Množství energie';
        if (Util.getLang() == 'sk') energyAmountText = 'Množstvo energie';

        //

        const mealNumber = Util.getLectureTaskMealCount();
        const showNutrikucharEnergyBalance = mealNumber > 1;

        //

        const attribRowCls = 'flex items-center py-1 text-xs border-b border-slate-100 [&>p]:m-0 [&>p]:flex-1 [&>aside]:ml-auto [&>aside]:text-right [&>aside]:font-medium [&>aside]:text-slate-700';

        if (typeof data.evaluationResults !== 'undefined') {
            if (typeof data.evaluationResults.results !== 'undefined') {

                const thisMealIsEmpty = data.evaluationResults.results.weight === 0 && data.evaluationResults.results.energy_kcal === 0;

                Object.keys(data.evaluationResults.results).map(attr => {
                    if (attr == '') return;
                    if (attr === 'fats_unsaturated') attr = 'fats_unsaturated_total';
                    if (attr === 'energy_balance_kcal') attr = 'kcal_kj';

                    if (typeof watchAttrs === 'object') {
                        if (watchAttrs.includes(attr)) {

                            if (attr == 'kcal_kj') {
                                let kcal = data.evaluationResults.results['energy_balance_kcal'] + '';
                                let kj = data.evaluationResults.results['energy_balance_kj'] + '';
                                if (kcal > 0 && kcal.substr(0, 1) !== '+') kcal = '+' + kcal;
                                if (kj > 0 && kj.substr(0, 1) !== '+') kj = '+' + kj;

                                currEl = (
                                    <div className={attribRowCls}>
                                        <p>{energyAmountText}:</p>
                                        <aside>{data.evaluationResults.results['energy_kcal'] * 1}{'\u00A0'}kcal</aside>
                                    </div>
                                );
                                watchAttribs.push(currEl);
                                watchAttribsByKey['energy_kcal'] = currEl;

                                currEl = (
                                    <div className={attribRowCls}>
                                        <p>{energyAmountText}:</p>
                                        <aside>{data.evaluationResults.results['energy_kj']}{'\u00A0'}kJ</aside>
                                    </div>
                                );
                                watchAttribs.push(currEl);
                                watchAttribsByKey['energy_kj'] = currEl;

                                if (showNutrikucharEnergyBalance) {
                                    currEl = (
                                        <div className={attribRowCls}>
                                            <p>{data.globals.attributes['energy_balance_kcal']}:</p>
                                            <aside>{kcal}{'\u00A0'}{data.globals.attribute_units['energy_balance_kcal']}</aside>
                                        </div>
                                    );
                                    watchAttribs.push(currEl);
                                    watchAttribsByKey['energy_balance_kcal'] = currEl;

                                    currEl = (
                                        <div className={attribRowCls}>
                                            <p>{data.globals.attributes['energy_balance_kj']}:</p>
                                            <aside>{kj}{'\u00A0'}{data.globals.attribute_units['energy_balance_kj']}</aside>
                                        </div>
                                    );
                                    watchAttribs.push(currEl);
                                    watchAttribsByKey['energy_balance_kj'] = currEl;
                                }

                            } else {

                                let resval, textEval;

                                switch (attr) {

                                    case 'gi':
                                        resval = data.evaluationResults.results[attr];
                                        textEval = __('nízký');
                                        if (resval > 50) textEval = __('střední');
                                        if (resval > 70) textEval = __('vysoký');
                                        if (thisMealIsEmpty) textEval = '-';
                                        currEl = (
                                            <div className={attribRowCls}>
                                                <p>{data.globals.attributes[attr]}:</p>
                                                <aside>{textEval}</aside>
                                            </div>
                                        );
                                        watchAttribs.push(currEl);
                                        watchAttribsByKey[attr] = currEl;
                                        watchAttribsAlreadyFilled.push(attr);
                                        break;

                                    case 'gn':
                                        resval = data.evaluationResults.results[attr];
                                        textEval = __('nízká');
                                        if (resval > 80) textEval = __('střední');
                                        if (resval > 119) textEval = __('vysoká');
                                        if (thisMealIsEmpty) textEval = '-';
                                        currEl = (
                                            <div className={attribRowCls}>
                                                <p>{data.globals.attributes[attr]}:</p>
                                                <aside>{textEval}</aside>
                                            </div>
                                        );
                                        watchAttribs.push(currEl);
                                        watchAttribsByKey[attr] = currEl;
                                        watchAttribsAlreadyFilled.push(attr);
                                        break;

                                    default:
                                        if (['acidobasicity', 'digestibility', 'energy', 'thermic_effect', 'thermiceffect'].includes(attr)) {
                                            let attrFinal = attr;
                                            if (attrFinal === 'energy') attrFinal = 'energytendency';
                                            if (attrFinal === 'thermic_effect') attrFinal = 'thermiceffect';
                                            let opts = data.globals.evaluationTexts[attrFinal];
                                            let text = opts[Math.round(data.evaluationResults.results[attr + '_n'])];
                                            if (thisMealIsEmpty) text = '-';
                                            currEl = (
                                                <div className={attribRowCls}>
                                                    <p>{data.globals.attributes[attr]}:</p>
                                                    <aside>{text}{'\u00A0'}{data.globals.attribute_units[attr]}</aside>
                                                </div>
                                            );
                                            watchAttribs.push(currEl);
                                            watchAttribsByKey[attr] = currEl;
                                            watchAttribsAlreadyFilled.push(attr);
                                        } else {
                                            if (showRI.includes(attr)) {
                                                if (typeof data.evaluationResults.ddd[attr] !== 'undefined') {
                                                    let RIpct = data.evaluationResults.ddd[attr].percent;
                                                    currEl = (
                                                        <div className={attribRowCls}>
                                                            <p>{data.globals.attributes[attr]}:</p>
                                                            <aside>{RIpct}{'\u00A0'}% RI</aside>
                                                        </div>
                                                    );
                                                    watchAttribs.push(currEl);
                                                    watchAttribsByKey[attr] = currEl;
                                                    watchAttribsAlreadyFilled.push(attr);
                                                }
                                            } else {
                                                currEl = (
                                                    <div className={attribRowCls}>
                                                        <p>{data.globals.attributes[attr]}:</p>
                                                        <aside>{data.evaluationResults.results[attr]}{'\u00A0'}{data.globals.attribute_units[attr]}</aside>
                                                    </div>
                                                );
                                                watchAttribs.push(currEl);
                                                watchAttribsByKey[attr] = currEl;
                                                watchAttribsAlreadyFilled.push(attr);

                                                if (attr == 'added_sugars') {
                                                    let sugarCubes = data.evaluationResults.results[attr] / 4;
                                                    sugarCubes = Math.ceil(sugarCubes * 10) / 10;
                                                    currEl = (
                                                        <div className={attribRowCls}>
                                                            <p>- odpovídá kostkám cukru:</p>
                                                            <aside>{sugarCubes}</aside>
                                                        </div>
                                                    );
                                                    watchAttribs.push(currEl);
                                                    watchAttribsByKey['added_sugar_cubes'] = currEl;
                                                }
                                            }
                                        }
                                        break;
                                }
                            }
                        }
                    }

                    Object.keys(showRatios).map(r => {
                        if (data.appModeData.watchAttributes.includes(r)) return;
                        if (showRatios[r].after === attr && showRatios[r].depends_on.length === 0) {
                            currEl = (
                                <div className={attribRowCls}>
                                    <p>{data.globals.attributes[r]}:</p>
                                    <aside>{data.evaluationResults.results[r]}</aside>
                                </div>
                            );
                            watchAttribs.push(currEl);
                            watchAttribsByKey[attr] = currEl;
                            watchAttribsAlreadyFilled.push(r);
                        }
                    });

                });

                if (watchAttribs.length > 0) {

                    let results = data.evaluationResults.results;
                    let chartColours = ["#96c999", "#eb714a", "#eae869", "#8bcbce", "#c694be"];

                    let enProt = results["proteins"] * 4.18;
                    let enCarb = results["carbohydrates"] * 4.18;
                    let enFats = results["fats"] * 9;
                    let enFibr = results["fiber"] * 2;

                    let total = enProt + enCarb + enFats + enFibr;
                    if (total == 0) total = 0.00001;

                    enProt = Math.round((enProt / total) * 100);
                    enCarb = Math.round((enCarb / total) * 100);
                    enFats = Math.round((enFats / total) * 100);
                    enFibr = 100 - enProt - enCarb - enFats;

                    let chart2 = Util.pieChart(
                        [enProt, enCarb, enFats, enFibr],
                        100, 100, 95,
                        chartColours
                    );

                    graph = (
                        <div className="mb-4">
                            <div className={`${taskBoxHeaderCls} relative`}>
                                <h3 className={taskBoxTitleCls}>{__('Energetické poměry makroživin')}</h3>
                                <label className={taskHelpLabelCls} htmlFor="lt-cb5">?</label>
                                <input className="peer hidden" type="checkbox" id="lt-cb5" />
                                <div className={taskHelpPanelCls}>
                                    <p className="m-0">{warningText5}</p>
                                </div>
                            </div>
                            <svg viewBox="0 0 400 200" width="100%" height="100%">
                                {chart2}
                                <rect x="220" y="40" width="20" height="20" style={{ fill: chartColours[0], strokeWidth: 0 }} />
                                <text fontSize="1.4em" x="250" y="55" fill="#444">{__('Bílkoviny')} ({parseFloat(enProt)} %)</text>
                                <rect x="220" y="70" width="20" height="20" style={{ fill: chartColours[1], strokeWidth: 0 }} />
                                <text fontSize="1.4em" x="250" y="85" fill="#444">{__('Sacharidy')} ({parseFloat(enCarb)} %)</text>
                                <rect x="220" y="100" width="20" height="20" style={{ fill: chartColours[2], strokeWidth: 0 }} />
                                <text fontSize="1.4em" x="250" y="115" fill="#444">{__('Tuky')} ({parseFloat(enFats)} %)</text>
                                <rect x="220" y="130" width="20" height="20" style={{ fill: chartColours[3], strokeWidth: 0 }} />
                                <text fontSize="1.4em" x="250" y="145" fill="#444">{__('Vláknina')} ({parseFloat(enFibr)} %)</text>
                            </svg>
                        </div>
                    );
                }
            }
        }

        evaluationPanelBeforeContent = (
            <>
                {allFoodGroupsMustHaveNamesWarning}
                {allFoodGroupsMustHaveDescriptionsWarning}
                {taskCompletedInfo}
                <div className="px-4 py-2 text-xs">
                    <div className="mb-3">{tasksList}</div>
                </div>
            </>
        );

        let watchAttribsSorted = [];

        let attributeKey, attributeUnit;
        for (let groupKey in data.globals.evaluation_groups_opened) {
            let subItems = [];
            for (let subGroup in globals.evaluation_groups[groupKey]) {
                attributeKey = globals.evaluation_groups[groupKey][subGroup];
                attributeUnit = globals.attribute_units[attributeKey];
                if (typeof watchAttribsByKey[attributeKey] == "undefined") continue;
                subItems.push(watchAttribsByKey[attributeKey]);
            }
            if (subItems.length > 0) {
                watchAttribsSorted.push(
                    <div key={groupKey} className="mb-1">
                        <input className="watch-toggle peer hidden" id={`watchgrp_${groupKey}`} type="checkbox" />
                        <label className="cursor-pointer text-xs font-semibold text-slate-600 flex items-center gap-1 py-1" htmlFor={`watchgrp_${groupKey}`}>
                            <i className="fa fa-angle-right text-xs text-slate-400"></i>
                            {groupKey}
                        </label>
                        <div className="hidden peer-checked:block pl-2">{subItems}</div>
                    </div>
                );
            }
        }

        if (watchAttribs.length > 0) {
            evaluationPanelBeforeContent = (
                <>
                    {allFoodGroupsMustHaveNamesWarning}
                    {allFoodGroupsMustHaveDescriptionsWarning}
                    {taskCompletedInfo}
                    <div className="px-4 py-2 text-xs">
                        <div className="mb-3">{tasksList}</div>
                        <div className="mb-4">
                            <div className={`${taskBoxHeaderCls} relative`}>
                                <h3 className={taskBoxTitleCls}>Sledujte</h3>
                                <label className={taskHelpLabelCls} htmlFor="lt-cb4">?</label>
                                <input className="peer hidden" type="checkbox" id="lt-cb4" />
                                <div className={taskHelpPanelCls}>
                                    <p className="m-0">{warningText4}</p>
                                </div>
                            </div>
                            {watchAttribsSorted}
                        </div>
                        <div className="mb-4">
                            {graph}
                        </div>
                    </div>
                </>
            );
        }

        if (isNutriKucharMode) {
            const nkEvaluation = getNutriKucharEvaluationDetails(data);
            const isNutriKucharTaskComplete = (
                nkEvaluation.requiredFoods.length > 0 &&
                nkEvaluation.allFoodsOk &&
                nkEvaluation.missingFoods.length === 0 &&
                nkEvaluation.wrongAmount.length === 0 &&
                nkEvaluation.wrongPreparations.length === 0 &&
                nkEvaluation.excessFoods.length === 0 &&
                allFoodGroupsHaveNames &&
                allFoodGroupsHaveDescriptions
            );

            const diagnostics = getNutriKucharCompletionDiagnostics(
                data,
                nkEvaluation,
                allFoodGroupsHaveNames,
                allFoodGroupsHaveDescriptions,
                missingNamesMealLabels,
                missingDescriptionsMealLabels
            );
            const diagnosticsKey = diagnostics ? JSON.stringify(diagnostics) : '';
            if (!isNutriKucharTaskComplete && diagnostics && diagnosticsKey !== nutriKucharDiagnosticsRef.current) {
                console.debug('[NUTRIKUCHAR] Aktuální úkol není splněný.', diagnostics);
                nutriKucharDiagnosticsRef.current = diagnosticsKey;
            }
            if (isNutriKucharTaskComplete) {
                nutriKucharDiagnosticsRef.current = '';
            }

            if (isCurrentlyCompleted !== isNutriKucharTaskComplete) {
                dispatch(setLectureTaskComplete(isNutriKucharTaskComplete));
            }

            const nkSectionClass = 'mb-4';
            const nkHeadingClass = 'text-xs m-0 text-slate-500 uppercase font-extrabold tracking-wider mb-2';
            const nkListClass = 'space-y-1 text-xs text-slate-700';
            const nkFoodTitleClass = 'm-0';
            const nkFoodPrepClass = 'm-0 ml-4 text-[0.92em]';
            const nkWarningRowClass = 'm-0 flex items-center gap-2 text-red-500';

            const nkWarnings = [];
            if (!allFoodGroupsHaveNames) {
                nkWarnings.push(__('Doplňte názvy receptu'));
            }
            if (!allFoodGroupsHaveDescriptions) {
                nkWarnings.push(__('Doplňte popis přípravy receptu'));
            }

            const nkWarningsSection = nkWarnings.length > 0 ? (
                <div className={nkSectionClass}>
                    <h3 className={nkHeadingClass}>{__('Upozornění')}</h3>
                    <div className="space-y-2 text-xs">
                        {nkWarnings.map((warning, index) => (
                            <p className={nkWarningRowClass} key={`${warning}-${index}`}>
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[0.7rem] leading-none">!</span>
                                <span>{warning}</span>
                            </p>
                        ))}
                    </div>
                </div>
            ) : false;

            const nkFoodsSection = nkEvaluation.requiredFoods.length > 0 ? (
                <div className={`${nkSectionClass} pb-1`}>
                    <h3 className={nkHeadingClass}>{__('Potraviny')}</h3>
                    <div className="space-y-2 text-xs text-black">
                        {nkEvaluation.requiredFoods.map((item, index) => (
                            <div key={`${item.title}-${index}`}>
                                <p className={nkFoodTitleClass}>
                                    {item.isOk && <span className="mr-1 text-green-600 font-bold">✓</span>}
                                    <span className={item.isOk ? 'text-green-600 font-bold' : ''}>{item.title}</span>
                                </p>
                                <p className={`${nkFoodPrepClass} ${item.isOk ? 'text-green-600' : ''}`}>
                                    {item.preparations.length > 0 ? item.preparations.join(', ') : __('žádné úpravy')}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : false;

            const renderNkSection = (title, items) => {
                if (items.length === 0) return false;
                return (
                    <div className={nkSectionClass} key={title}>
                        <h3 className={nkHeadingClass}>{title}</h3>
                        <div className={nkListClass}>
                            {items.map((item, index) => <p className="m-0" key={`${title}-${index}`}>{item}</p>)}
                        </div>
                    </div>
                );
            };

            const nkSections = [
                renderNkSection(__('Chybějící potraviny'), nkEvaluation.missingFoods),
                renderNkSection(__('Nadbytečné potraviny'), nkEvaluation.excessFoods),
            ].filter(Boolean);

            const nkWatchSection = watchAttribs.length > 0 ? (
                <>
                    <div className="mb-4">
                        <div className={`${taskBoxHeaderCls} relative`}>
                            <h3 className={taskBoxTitleCls}>Sledujte</h3>
                            <label className={taskHelpLabelCls} htmlFor="lt-cb4-nk">?</label>
                            <input className="peer hidden" type="checkbox" id="lt-cb4-nk" />
                            <div className={taskHelpPanelCls}>
                                <p className="m-0">{warningText4}</p>
                            </div>
                        </div>
                        {watchAttribsSorted}
                    </div>
                    <div className="mb-4">
                        {graph}
                    </div>
                </>
            ) : false;

            evaluationPanelBeforeContent = (
                <>
                    <div className="px-4 py-2 text-xs">
                        {!isSampleTask && (
                            <>
                                {nkFoodsSection}
                                {nkSections.length > 0 && <div className="mb-3">{nkSections}</div>}
                                {nkWarningsSection}
                            </>
                        )}
                        {nkWatchSection}
                    </div>
                </>
            );
        }
    }

    let profileInfoContent = 'Váš profil';
    if (typeof data.appModeData.profileId === 'number') {
        let fpid = data.appModeData.profileId;
        profileInfoContent = globals.profiles[fpid].name;
    }

    if (typeof data.appModeData.profileOverride === 'object') {
        profileInfoContent = data.appModeData.profileOverride.name;
    }

    let saveWarning = false;
    if (showSaveWarning) {
        saveWarning = (
            <span className="fixed left-1/2 -translate-x-1/2 top-4 bg-amber-300 text-amber-900 rounded-xl text-sm font-semibold py-2 px-6 inline-block leading-none shadow-lg z-50 [animation:sw_6s_forwards]">
                {`Nezapomeňte rozpracovaný ${taskNounAcc} průběžně ukládat!`}
            </span>
        );
    }

    const navBtnClass = 'whitespace-nowrap bg-white py-2 px-4 rounded-lg border border-slate-200 text-slate-700 no-underline flex items-center text-sm font-medium cursor-pointer hover:bg-slate-50 transition-colors shadow-sm gap-1.5';
    const navBtnAccentClass = 'whitespace-nowrap bg-green-500 py-2 px-4 rounded-lg border border-green-500 text-white no-underline flex items-center text-sm font-medium cursor-pointer hover:bg-green-600 transition-colors shadow-sm gap-1.5';

    let dayTitle = (
        <h1 className="text-xl font-bold text-slate-800 leading-tight mt-0.5">
            {__('Úkol k lekci')}: <span>{lectureInfo.title}</span>
        </h1>
    );
    const seminarWorkData = data.appModeData?.lectureInfoData?.seminar_work_data;

    const infoPillCls = 'py-2 px-4 bg-white border border-slate-200 rounded-lg text-left text-sm flex items-center shadow-sm';
    const groupLinkClass = 'text-green-600 cursor-pointer font-semibold hover:underline';
    let courseTitle = lectureInfo.course_title;

    if (typeof seminarWorkData === 'object') {
        courseTitle = 'Vypracování seminární práce';
        if (seminarWorkData.is_assignment === 1) {
            courseTitle = 'Zadání seminární práce';
        }
    }

    if (Util.getLang() == 'sk') {
        if (typeof seminarWorkData === 'object') {
            courseTitle = 'Vypracovanie seminárnej práce';
            if (seminarWorkData.is_assignment === 1) {
                courseTitle = 'Zadanie seminárnej práce';
            }
        }
    }

    const isSk = Util.getLang() === 'sk';
    const createTitle = isSk ? `Tvorba ${taskNounGen}` : `Tvorba ${taskNounGen}`;
    const sampleTitle = isSk ? `Vzorový ${taskNounNom}` : `Vzorový ${taskNounNom}`;
    const adjustedSampleTitle = isSk ? `Upravený ${taskNounNom}` : `Upravený ${taskNounNom}`;
    const analysisTitle = isSk ? `Rozbor ${taskNounGen}` : `Rozbor ${taskNounGen}`;

    if (isNutriKucharMode) {
        courseTitle = isSk ? 'Nutrikuchár - úloha k lekcii' : 'Nutrikuchař - úkol k lekci';
        dayTitle = (
            <h1 className="text-2xl font-bold text-slate-800 leading-tight mt-0.5">
                <span>{lectureInfo.title}</span>
            </h1>
        );
    }

    // ---- 4-step guide ----
    const isSample = isSampleTask;
    const isComplete = data.lectureTaskComplete;
    const isSaved = data.lectureTaskCompleteSaved;
    const ldInfo = data.appModeData.lectureInfoData;
    const isCourse46 = typeof ldInfo.course_id === 'number' && ldInfo.course_id === 46;
    const isSeminarWorkWriteMode = Util.isCurrentTaskSeminarWork() && !Util.isCurrentTaskSeminarWorkAssignment();
    const isOneFoodToComplete_sg = (
        typeof ldInfo.lecture_data !== 'undefined' &&
        typeof ldInfo.lecture_data.custom_settings !== 'undefined' &&
        ldInfo.lecture_data.custom_settings !== null &&
        typeof ldInfo.lecture_data.custom_settings === 'object' &&
        ldInfo.lecture_data.custom_settings.includes('one_food_to_complete_task')
    );
    const hasSampleMenu = !Util.isCurrentTaskSeminarWork() && !isOneFoodToComplete_sg && !isCourse46;
    const showStepGuide = !isCourse46 && (
        typeof seminarWorkData !== 'object' ||
        (isSeminarWorkWriteMode && (isComplete || isSaved))
    );

    let activeStep = 1;
    let totalGuideSteps = 3;
    let guideStepDefs = [];

    if (isNutriKucharMode) {
        activeStep = isSample || isComplete ? 2 : 1;
        totalGuideSteps = 2;
        guideStepDefs = [
            { num: 1, title: createTitle },
            { num: 2, title: adjustedSampleTitle },
        ];
    } else {
        if (isSample) activeStep = 4;
        else if (isSaved) activeStep = 3;
        else if (isComplete) activeStep = 2;
        totalGuideSteps = hasSampleMenu ? 4 : 3;
        guideStepDefs = hasSampleMenu ? [
            { num: 1, title: createTitle },
            { num: 2, title: isSk ? 'Uloženie úlohy' : 'Uložení úkolu' },
            { num: 3, title: sampleTitle },
            { num: 4, title: analysisTitle },
        ] : [
            { num: 1, title: createTitle },
            { num: 2, title: isSk ? 'Uloženie úlohy' : 'Uložení úkolu' },
            { num: 3, title: isSk ? 'Hotovo' : 'Hotovo' },
        ];
    }

    const effectiveActiveStep = Math.min(activeStep, totalGuideSteps);

    const stepBannerConfigs = {
        1: {
            bg: 'bg-blue-50', border: 'border-blue-100', iconBg: 'bg-blue-500', icon: 'fa-pencil',
            title: isSk ? `Vytvorte ${taskNounAcc} pre cieľový profil` : `Vytvořte ${taskNounAcc} pro cílový profil`,
            sub: isSk ? 'Proštudujte zadanie a splnte všetky požiadavky.' : 'Prostudujte zadání a splňte všechny požadavky.',
            buttons: (
                <>
                    <button onClick={openAssignmentInfo} className="whitespace-nowrap text-sm font-medium text-blue-700 bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer">
                        <i className="fa fa-list-alt mr-1.5"></i>
                        {isSk ? 'Zadanie' : 'Zadání'}
                    </button>
                    <button onClick={openProfileInfo} className="whitespace-nowrap text-sm font-medium text-slate-700 bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-1.5">
                        <i className="fa fa-user text-xs text-slate-400"></i>
                        <span>{profileInfoContent}</span>
                    </button>
                </>
            ),
        },
        2: {
            bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-600', icon: 'fa-check',
            title: isSk ? 'Výborne! Vaša úloha je splnená.' : 'Výborně! Váš úkol je splněn.',
            sub: isSeminarWorkWriteMode
                ? (isSk ? 'Nezabudnite si úlohu uložiť.' : 'Nezapomeňte si úkol uložit.')
                : (isSk ? 'Nezabudnite uložiť postup, než budete pokračovať ďalej.' : 'Nezapomeňte uložit postup, než budete pokračovat dál.'),
            buttons: isSeminarWorkWriteMode ? (
                <button onClick={() => openSaveLectureTaskPanel()} className="whitespace-nowrap bg-green-600 text-white font-bold text-sm px-5 py-2 rounded-xl cursor-pointer border-none hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm">
                    <i className="fa fa-save"></i>
                    {isSk ? 'Uložiť' : 'Uložit'}
                </button>
            ) : (
                <>
                    <button onClick={() => openSaveLectureTaskPanel()} className="whitespace-nowrap bg-green-600 text-white font-bold text-sm px-5 py-2 rounded-xl cursor-pointer border-none hover:bg-green-700 transition-colors flex items-center gap-2 shadow-sm">
                        <i className="fa fa-save"></i>
                        {isSk ? 'Uložiť' : 'Uložit'}
                    </button>
                    <button onClick={() => dispatch(setLectureTaskCompleteSaved(true))} className="whitespace-nowrap text-sm font-medium text-slate-500 bg-white border border-slate-200 px-3 py-2 rounded-xl cursor-pointer hover:bg-slate-50 hover:text-slate-700 transition-colors flex items-center gap-1.5">
                        <i className="fa fa-check text-xs text-green-500"></i>
                        {isSk ? 'Už mám uložené, pokračovať' : 'Již mám uloženo, pokračovat'}
                    </button>
                </>
            ),
        },
        3: hasSampleMenu ? {
            bg: 'bg-indigo-50', border: 'border-indigo-200', iconBg: 'bg-indigo-500', icon: 'fa-list-alt',
            title: isSk ? `Otvorte vzorový ${taskNounAcc}` : `Otevřete vzorový ${taskNounAcc}`,
            sub: isSk ? 'Dôležitá súčasť výuky! Porovnajte vzorové riešenie so svojím.' : 'Důležitá součást výuky! Porovnejte vzorové řešení se svým.',
            buttons: (
                <button onClick={openLectureTaskSampleMenu} className="whitespace-nowrap bg-indigo-600 text-white font-bold text-sm px-5 py-2 rounded-xl cursor-pointer border-none hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm">
                    <i className="fa fa-list-alt"></i>
                    {isSk ? `Otvoriť vzorový ${taskNounAcc}` : `Otevřít vzorový ${taskNounAcc}`}
                </button>
            ),
        } : {
            bg: 'bg-green-50', border: 'border-green-100', iconBg: 'bg-green-500', icon: 'fa-check',
            title: isSk ? 'Výborne! Vaša úloha bola úspešne uložená.' : 'Výborně! Váš úkol byl úspěšně uložen.',
            sub: isSeminarWorkWriteMode
                ? (isSk ? `Váš seminárny ${taskNounNom} je uložený.` : `Váš seminární ${taskNounNom} je uložený.`)
                : (isSk ? 'Teraz môžete pokračovať ďalej.' : 'Nyní můžete pokračovat dál.'),
            buttons: null,
        },
        4: {
            bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-500', icon: 'fa-comment',
            title: isSk ? `Prečítajte si rozbor vzorového ${taskNounGen}` : `Přečtěte si rozbor vzorového ${taskNounGen}`,
            sub: isSk ? `Preštudujte špecifiká vzorového ${taskNounGen}.` : `Prostudujte specifika vzorového ${taskNounGen}.`,
            buttons: ldInfo.lecture_data?.demo_lecture_text ? (
                <button onClick={openLectureTaskSampleText} className="whitespace-nowrap bg-amber-500 text-white font-bold text-sm px-5 py-2 rounded-xl cursor-pointer border-none hover:bg-amber-600 transition-colors flex items-center gap-2 shadow-sm">
                    <i className="fa fa-comment"></i>
                    {isSk ? 'Zobraziť rozbor' : 'Zobrazit rozbor'}
                </button>
            ) : null,
        },
    };
    const nutriKucharBannerConfigs = {
        1: {
            bg: 'bg-blue-50', border: 'border-blue-100', iconBg: 'bg-blue-500', icon: 'fa-pencil',
            title: isSk
                ? `Vytvorte ${taskNounAcc}${isSingleDish ? '' : ' pre cieľový profil'}`
                : `Vytvořte ${taskNounAcc}${isSingleDish ? '' : ' pro cílový profil'}`,
            sub: isSk ? 'Použite zadanie, úlohu priebežne ukladajte.' : 'Použijte zadání, úlohu průběžně ukládejte.',
            buttons: null,
        },
        2: {
            bg: 'bg-indigo-50', border: 'border-indigo-200', iconBg: 'bg-indigo-500', icon: 'fa-list-alt',
            title: isSample
                ? adjustedSampleTitle
                : (isSk ? 'Výborne! Vaša úloha je splnená.' : 'Výborně! Váš úkol je splněn.'),
            sub: isSample
                ? (isSk ? `Porovnajte pôvodný ${taskNounAcc} s upraveným.` : `Porovnejte původní ${taskNounAcc} s upraveným.`)
                : (isSk ? `Teraz si môžete otvoriť upravený ${taskNounAcc}.` : `Nyní si můžete otevřít upravený ${taskNounAcc}.`),
            buttons: isSample ? (
                <button onClick={openLectureTaskSampleText} className="whitespace-nowrap bg-indigo-600 text-white font-bold text-sm px-5 py-2 rounded-xl cursor-pointer border-none hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm">
                    <i className="fa fa-comment"></i>
                    {isSk ? `Tajomstvo ${taskNounGen}` : `Tajemství ${taskNounGen}`}
                </button>
            ) : (
                <button onClick={openLectureTaskSampleMenu} className="whitespace-nowrap bg-indigo-600 text-white font-bold text-sm px-5 py-2 rounded-xl cursor-pointer border-none hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm">
                    <i className="fa fa-list-alt"></i>
                    {isSk ? `Otvoriť upravený ${taskNounAcc}` : `Otevřít upravený ${taskNounAcc}`}
                </button>
            ),
        },
    };
    const activeBannerConfig = isNutriKucharMode
        ? nutriKucharBannerConfigs[effectiveActiveStep]
        : stepBannerConfigs[effectiveActiveStep];
    const activeStepIconBg = isNutriKucharMode
        ? nutriKucharBannerConfigs[effectiveActiveStep].iconBg
        : activeBannerConfig.iconBg;

    let topNavButtons = (
        <>
            <a className={navBtnAccentClass} onClick={openSaveLectureTaskPanel}>
                <i className="fa fa-save text-xs"></i>
                {__('Uložit')}
            </a>
            <a className={navBtnClass} onClick={openLoadLectureTaskPanel}>
                <i className="fa fa-folder-open text-xs"></i>
                {__('Načíst')}
            </a>
        </>
    );

    if (isNutriKucharMode) {
        topNavButtons = false;
    }

    if (typeof data.appModeData.lectureInfoData.course_id === 'number') {
        if (data.appModeData.lectureInfoData.course_id === 46) {
            topNavButtons = false;
        }
    }

    const nkHeaderActions = isNutriKucharMode ? (
        <div className="flex flex-col items-end gap-2 shrink-0 pt-1">
            <p className="m-0 text-sm font-semibold text-slate-800">
                {`Krok ${effectiveActiveStep}/${totalGuideSteps} - ${guideStepDefs[effectiveActiveStep - 1].title}`}
            </p>
            <div className="flex gap-2 flex-wrap justify-end">
                <a className={navBtnClass} onClick={openAssignmentInfo}>
                    <i className="fa fa-file-text-o text-xs"></i>
                    {__('Zadání')}
                </a>
                <a className={navBtnClass} onClick={openLoadLectureTaskPanel}>
                    <i className="fa fa-upload text-xs"></i>
                    {isSk ? 'Načítať úlohu' : 'Načíst úkol'}
                </a>
                <a className={navBtnAccentClass} onClick={openSaveLectureTaskPanel}>
                    <i className="fa fa-download text-xs"></i>
                    {isSk ? 'Uložiť úlohu' : 'Uložit úkol'}
                </a>
            </div>
        </div>
    ) : false;

    return (
        <>
            <div className="w-full flex flex-col bg-slate-50 page-plan">

                {/* Page header */}
                <div className="bg-white border-b border-slate-200 px-4">
                    <div className="max-w-[1180px] mx-auto py-4 flex items-start justify-between gap-4">
                        {typeof seminarWorkData === 'object' ? (
                            <SeminarWorkHeader
                                lectureInfo={lectureInfo}
                                seminarWorkData={seminarWorkData}
                                onSave={openSaveLectureTaskPanel}
                                onLoad={openLoadLectureTaskPanel}
                                onOpenLongtermEvaluation={openSeminarWorkLongtermEvaluationPanel}
                            />
                        ) : (
                            <>
                                <div className="min-w-0">
                                    <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide mb-0.5">{courseTitle}</p>
                                    {dayTitle}
                                </div>
                                {nkHeaderActions && nkHeaderActions}
                                {topNavButtons && (
                                    <div className="flex gap-2 flex-wrap items-center shrink-0 pt-1">
                                        {topNavButtons}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Sub-header: step guide */}
                <div className="bg-white border-b border-slate-100 px-4">
                    <div className="max-w-[1180px] mx-auto">
                        {showStepGuide ? (
                            <>
                                {/* Step progress indicators */}
                                <div className="flex items-center py-2.5 gap-0 overflow-x-auto">
                                    {guideStepDefs.map((step, i) => (
                                        <React.Fragment key={step.num}>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${step.num < effectiveActiveStep ? 'bg-green-500 text-white' :
                                                        step.num === effectiveActiveStep ? activeStepIconBg + ' text-white' :
                                                            'bg-slate-200 text-slate-400'
                                                    }`}>
                                                    {step.num < effectiveActiveStep
                                                        ? <i className="fa fa-check text-[9px]" />
                                                        : step.num}
                                                </div>
                                                <span className={`text-xs whitespace-nowrap ${step.num < effectiveActiveStep ? 'text-green-600 font-medium' :
                                                        step.num === effectiveActiveStep ? 'text-slate-800 font-semibold' :
                                                            'text-slate-400'
                                                    }`}>{step.title}</span>
                                            </div>
                                            {i < guideStepDefs.length - 1 && (
                                                <div className="h-px bg-slate-200 mx-2 flex-1 min-w-4" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                                {/* Active step action banner */}
                                <div className={`rounded-xl border mb-3 ${activeBannerConfig.bg} ${activeBannerConfig.border}`}>
                                    <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white ${activeBannerConfig.iconBg}`}>
                                            <i className={`fa ${activeBannerConfig.icon} text-sm`}></i>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="m-0 text-sm font-bold text-slate-800">{activeBannerConfig.title}</p>
                                            {activeBannerConfig.sub && <p className="m-0 text-xs text-slate-500 mt-0.5">{activeBannerConfig.sub}</p>}
                                        </div>
                                        {activeBannerConfig.buttons && (
                                            <div className="flex gap-2 flex-wrap shrink-0">
                                                {activeBannerConfig.buttons}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="py-2.5 flex items-center gap-3 flex-wrap">
                                <div className={infoPillCls}>
                                    <div>
                                        <p className="m-0 text-xs text-slate-400 uppercase font-semibold tracking-wide">{__('Cílový profil')}</p>
                                        <p className="m-0 mt-0.5 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                                            </svg>
                                            <span className="text-slate-700 font-medium">{profileInfoContent}</span>
                                            <a onClick={openProfileInfo} className={groupLinkClass + ' text-xs'}>{__('Více')}</a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main content */}
                <div className="flex flex-1 flex-col">
                    <div className="max-w-[1180px] mx-auto px-4 w-full">

                        {/* Mobile tabs */}
                        <div className="hidden max-[959px]:flex items-center justify-center py-3 gap-2 -mx-4 bg-white border-b border-slate-100">
                            <a onClick={() => setMobileTab('main')} className={`py-2 px-5 text-sm font-semibold rounded-lg cursor-pointer transition-colors ${mobileTab === 'main' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-700'}`}>Rozpis</a>
                            <a onClick={() => setMobileTab('evaluation')} className={`py-2 px-5 text-sm font-semibold rounded-lg cursor-pointer transition-colors ${mobileTab === 'evaluation' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-700'}`}>Vyhodnocení</a>
                        </div>

                        <div className="flex flex-1">
                            <div className="w-full flex justify-between">
                                <div className={`pt-6 order-2 grow flex flex-col pr-10 max-[959px]:pr-0 ${mobileTab !== 'main' ? 'max-[959px]:hidden' : ''}`}>
                                    <div className="flex flex-1 flex-col">
                                        <div className="flex justify-end mb-4 text-sm">
                                            {viewTypeSwitch2}
                                        </div>
                                        {plan}
                                    </div>
                                </div>
                                <div className="pt-6 order-3 sticky top-0 self-start max-h-screen overflow-y-auto">
                                    <EvaluationPanel
                                        beforeContent={evaluationPanelBeforeContent}
                                        wholeDay={true}
                                        quickResults={false}
                                        evaluationCollapsedAtStart={true}
                                        mainStyle={{ paddingTop: '2em' }}
                                        className={mobileTab !== 'evaluation' ? 'max-[959px]:hidden' : ''}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {saveWarning}
        </>
    );
}

export default LectureTaskPlan;
